import { Capacitor } from '@capacitor/core';
import { BASE_URL } from '../config';

export interface ShareResult {
  url: string;
  success: boolean;
}

export class ShareManager {
  /**
   * Generates a shareable URL for puzzles with server IDs or daily puzzles
   * @param serverId - The server-generated puzzle ID (e.g., X1234567)
   * @param dailyDate - The date string for daily puzzles (e.g., 2025-11-25)
   * @returns The shareable URL or a message if puzzle is not shareable
   */
  static async generateShareUrl(serverId: string | null, dailyDate?: string): Promise<string> {
    // Check if this is a daily puzzle
    if (dailyDate) {
      return `${BASE_URL}/daily/${dailyDate}`;
    }

    if (!serverId) {
      return 'This puzzle cannot be shared. Only uploaded puzzles can be shared.';
    }

    return `${BASE_URL}/X${serverId}`;
  }

  /**
   * Shares URL via Facebook
   */
  static async shareViaFacebook(url: string): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      // For native apps, use FB SDK or generic share sheet
      // For now, we'll use the generic share which will show FB as an option
      await this.shareGeneric(url, 'Check out this JoinSaw puzzle!');
    } else {
      // Web fallback: Open Facebook share dialog
      const fbShareUrl = new URL('https://www.facebook.com/sharer/sharer.php');
      fbShareUrl.searchParams.append('u', url);
      window.open(fbShareUrl.toString(), '_blank', 'width=600,height=400');
    }
  }

  /**
   * Shares URL via SMS
   */
  static async shareViaSMS(url: string): Promise<void> {
    const message = `Check out this JoinSaw puzzle! ${url}`;
    const smsUrl = `sms:?body=${encodeURIComponent(message)}`;
    window.location.href = smsUrl;
  }

  /**
   * Shares URL via Email
   */
  static async shareViaEmail(url: string): Promise<void> {
    const subject = 'Check out this JoinSaw puzzle!';
    const body = `I thought you might enjoy this jigsaw puzzle game:\n\n${url}\n\nHave fun!`;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  }

  /**
   * Copies URL to clipboard
   */
  static async copyToClipboard(url: string): Promise<boolean> {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        return true;
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textArea);
        return success;
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  }

  /**
   * Generic share using native share sheet (if available)
   */
  static async shareGeneric(url: string, text: string): Promise<void> {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'JoinSaw Puzzle',
          text: text,
          url: url
        });
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Web share failed:', error);
        }
      }
    } else {
      // Fallback: copy to clipboard
      const copied = await this.copyToClipboard(url);
      if (copied) {
        alert('Link copied to clipboard!');
      }
    }
  }
}
